import { useEffect, useMemo, useState } from "react";
import BlogForm from "../../components/Dashboard/BlogForm";
import BlogTable from "../../components/Dashboard/BlogTable";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import SearchBar from "../../components/UI/SearchBar";
import Loader from "../../components/UI/Loader";
import useBlogs from "../../hooks/useBlogs";
import {
  createBlog,
  updateBlog,
  deleteBlog,
  uploadMedia,
} from "../../services/blogService";

const BlogsPage = () => {
  const {
    adminBlogs,
    categories,
    adminLoading,
    error,
    setError,
    fetchAdminBlogs,
  } = useBlogs();

  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchAdminBlogs();
  }, [fetchAdminBlogs]);

  const posts = useMemo(() => {
    if (!keyword) return adminBlogs;

    return adminBlogs.filter((item) =>
      [item?.title, item?.subtitle, item?.slug, item?.excerpt, item?.meta_title]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(String(keyword).toLowerCase())
    );
  }, [adminBlogs, keyword]);

  const stats = useMemo(() => {
    const total = adminBlogs.length;
    const published = adminBlogs.filter((item) => item?.status === "published").length;
    const draft = adminBlogs.filter((item) => item?.status === "draft").length;
    const featured = adminBlogs.filter((item) => Boolean(item?.is_featured)).length;

    return { total, published, draft, featured };
  }, [adminBlogs]);

  const handleCreate = () => {
    setSelectedPost(null);
    setIsModalOpen(true);
    setError("");
    setSuccessMessage("");
  };

  const handleEdit = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    setError("");
    setSuccessMessage("");
  };

  const handleDelete = async (post) => {
    const ok = window.confirm(`Hapus artikel "${post.title}"?`);
    if (!ok) return;

    setActionLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await deleteBlog(post.id);
      setSuccessMessage("Artikel berhasil dihapus.");
      await fetchAdminBlogs();
    } catch (err) {
      setError(err?.message || "Gagal menghapus artikel.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async (payload) => {
    console.log("BlogsPage: Memulai proses submit...");
    setActionLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      let featuredImageUrl = payload.featured_image;
      let heroBgUrl = payload.hero_bg;

      if (payload.featured_image instanceof File) {
        console.log("BlogsPage: Mengupload Featured Image baru...");
        const response = await uploadMedia(payload.featured_image, "blog");

        const uploadedData = Array.isArray(response) ? response[0] : response;
        featuredImageUrl = uploadedData?.url || uploadedData?.file_path;

        console.log("BlogsPage: URL Gambar baru didapat:", featuredImageUrl);
      }

      if (payload.hero_bg instanceof File) {
        console.log("BlogsPage: Mengupload Hero BG baru...");
        const responseHero = await uploadMedia(payload.hero_bg, "hero");

        const uploadedHeroData = Array.isArray(responseHero) ? responseHero[0] : responseHero;
        heroBgUrl = uploadedHeroData?.url || uploadedHeroData?.file_path;
      }

      const submitData = {
        ...payload,
        featured_image: featuredImageUrl,
        hero_bg: heroBgUrl,
        faq_json:
          typeof payload.faq_json === "string"
            ? JSON.parse(payload.faq_json || "[]")
            : payload.faq_json,
        portfolio_json:
          typeof payload.portfolio_json === "string"
            ? JSON.parse(payload.portfolio_json || "[]")
            : payload.portfolio_json,
        is_active: payload.status === "published" ? "True" : "False",
      };

      console.log("BlogsPage: Mengirim data ke API Blog:", submitData);

      if (selectedPost?.id) {
        await updateBlog(selectedPost.id, submitData);
        setSuccessMessage("Artikel berhasil diperbarui!");
      } else {
        await createBlog(submitData);
        setSuccessMessage("Artikel baru berhasil dibuat!");
      }

      setIsModalOpen(false);
      setSelectedPost(null);
      await fetchAdminBlogs();
    } catch (err) {
      console.error("BlogsPage Error:", err);
      setError(err?.message || "Gagal menyimpan artikel.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div
        style={{
          background: "#ffffff",
          borderRadius: 24,
          padding: 24,
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
          marginBottom: 24,
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          className="dashboard-page__header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div>
            <h2 style={{ marginBottom: 8 }}>Kelola Blog</h2>
            <p style={{ margin: 0, color: "#64748b", lineHeight: 1.6 }}>
              Buat, edit, dan optimalkan artikel blog dengan struktur SEO lengkap
              untuk website Solusi IoT Cerdas.
            </p>
          </div>

          <Button onClick={handleCreate}>+ Tambah Artikel</Button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
          }}
        >
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 18,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>
              Total Artikel
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#0f172a" }}>
              {stats.total}
            </div>
          </div>

          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 18,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 13, color: "#15803d", marginBottom: 6 }}>
              Published
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#166534" }}>
              {stats.published}
            </div>
          </div>

          <div
            style={{
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: 18,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 13, color: "#c2410c", marginBottom: 6 }}>
              Draft
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#9a3412" }}>
              {stats.draft}
            </div>
          </div>

          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 18,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 13, color: "#2563eb", marginBottom: 6 }}>
              Featured
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1d4ed8" }}>
              {stats.featured}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          background: "#ffffff",
          borderRadius: 24,
          padding: 24,
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <SearchBar
              value={keyword}
              onChange={setKeyword}
              placeholder="Cari judul, slug, excerpt, atau meta title..."
            />
          </div>
        </div>

        {successMessage && (
          <div
            style={{
              marginBottom: 16,
              padding: "14px 16px",
              borderRadius: 14,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              color: "#166534",
              fontWeight: 600,
            }}
          >
            {successMessage}
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: "14px 16px",
              borderRadius: 14,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {(adminLoading || actionLoading) ? (
          <Loader text="Memuat data artikel..." />
        ) : (
          <BlogTable
            posts={posts}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          if (actionLoading) return;
          setIsModalOpen(false);
          setSelectedPost(null);
        }}
        title={selectedPost ? "Edit Artikel" : "Tambah Artikel Baru"}
        size="xl"
      >
        <BlogForm
          initialValues={selectedPost}
          categories={categories}
          onSubmit={handleSubmit}
          loading={actionLoading}
        />
      </Modal>
    </div>
  );
};

export default BlogsPage;